import React from 'react';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import { useFormContext } from 'react-hook-form';
import { EqualisationGeneralCapitalCallsFormValues } from './FormWrapper';
import { round } from 'lodash';
import useEqualiseGeneralCapital from '../../hooks/useEqualiseGeneralCapital';
import EqualisationsAllocationsTable, {
  ColumnConfig,
} from 'components/EqualisationElements/EqualisationsAllocationsTable';
import NumericInputCell from 'components/EqualisationElements/NumericInputCell';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';

type GeneralCapitalCallAllocationsFormProps = {
  fields: EqualisationGeneralCapitalCallsFormValues['generalCapitalCalls'][number]['newInvestorBreakdown'];
  totalAmountLcy: number;
  allocationRule: AllocationRule;
  actionIndex: number;
  baseCurrency: CurrencyCode;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    calledFundsLcy: number;
  }>;
};

const GeneralCapitalCallAllocationsForm: React.FC<
  GeneralCapitalCallAllocationsFormProps
> = ({
  fields,
  allocationRule,
  totalAmountLcy,
  actionIndex,
  baseCurrency,
  originalInvestorBreakdown,
}) => {
  const { control } =
    useFormContext<EqualisationGeneralCapitalCallsFormValues>();

  const {
    watchedFields,
    totalCalledFunds,
    totalAllocationPercentage,
    isPending,
    excludedInvestorNames,
    newInvestorBreakdownPath,
    onChangeAllocationRule,
    handleCalledFundsChange,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
  } = useEqualiseGeneralCapital({
    actionIndex,
    totalAmountLcy,
    originalInvestorBreakdown,
  });

  const columns: Array<ColumnConfig> = [
    {
      header: 'Investor',
      renderCell: (row) => row.investorName,
      renderFooter: () => null,
    },
    {
      header: 'Total Commitment',
      renderCell: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          currency: baseCurrency,
        }),
      renderFooter: () => null,
    },
    {
      header: 'Called funds (LCY)',
      renderCell: (_row, investorIndex) => (
        <NumericInputCell
          fixedDecimalScale={true}
          control={control}
          name={`${newInvestorBreakdownPath}.${investorIndex}.calledFundsLcy`}
          onChange={(value) => handleCalledFundsChange(value, investorIndex)}
          currency={baseCurrency}
          disabled={_row.excludeFromAllocation || isPending}
        />
      ),
      renderFooter: () =>
        totalCalledFunds.toLocaleString('en-US', {
          style: 'currency',
          currency: baseCurrency,
        }),
    },
    {
      header: 'Allocation % of called funds',
      renderCell: (_row, investorIndex) => (
        <NumericInputCell
          decimalScale={5}
          fixedDecimalScale={true}
          control={control}
          name={`${newInvestorBreakdownPath}.${investorIndex}.allocationPercentage`}
          onChange={(value) =>
            handleAllocationPercentageChange(value, investorIndex)
          }
          disabled={_row.excludeFromAllocation || isPending}
        />
      ),
      renderFooter: () => `${round(totalAllocationPercentage, 5)}%`,
    },
    {
      header: 'Call underpaid/(overpaid)',
      renderCell: (row) =>
        numberToCurrencyString(row.callOverpaidOrUnderpaid, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
      renderFooter: () => null,
    },
    {
      header: 'Exclude investor from allocation',
      renderCell: (_row, investorIndex) => (
        <CheckboxCell
          control={control}
          name={`${newInvestorBreakdownPath}.${investorIndex}.excludeFromAllocation`}
          onChange={(value) =>
            handleExcludeFromAllocationChange(value, investorIndex)
          }
        />
      ),
      renderFooter: () => null,
    },
  ];

  const rows = fields.map((field) => ({
    ...field,
    key: field.commitmentId,
  }));

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      footerLabel={`${watchedFields?.length || 0} investors`}
      isPending={isPending}
      allocationRule={allocationRule}
      allocationRuleOptions={[
        {
          value: AllocationRule.ByCommitmentAndClosingDate,
          label: `By commitment and closing date${
            excludedInvestorNames ? ` excluding ${excludedInvestorNames}` : ''
          }`,
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
      ]}
      onChangeAllocationRule={onChangeAllocationRule}
    />
  );
};

export default GeneralCapitalCallAllocationsForm;
